import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const SAMPLE_INPUT = `# Project README

A short description of the project.

## Getting Started

### Prerequisites

You need Node.js 18+ and npm 9+.

### Installation

\`\`\`bash
npm install
\`\`\`

## Usage

### CLI

Run \`npm start\` to start the dev server.

### API

Use the public API to integrate with your stack.

## Contributing

Pull requests are welcome.

## License

MIT
`;

const stripInlineMarkdown = (text) => {
  let out = text;
  out = out.replace(/`([^`]+)`/g, "$1");
  out = out.replace(/\*\*([^*]+)\*\*/g, "$1");
  out = out.replace(/__([^_]+)__/g, "$1");
  out = out.replace(/\*([^*]+)\*/g, "$1");
  out = out.replace(/_([^_]+)_/g, "$1");
  out = out.replace(/~~([^~]+)~~/g, "$1");
  out = out.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
  out = out.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
  out = out.replace(/<[^>]+>/g, "");
  return out.trim();
};

const stripDiacritics = (s) => {
  if (typeof s.normalize !== "function") return s;
  return s.normalize("NFKD").replace(/\p{M}+/gu, "");
};

const slugifyGitHub = (text) => {
  let s = text.toLowerCase();
  s = s.replace(/[^\p{L}\p{N}\s\-_]/gu, "");
  s = s.replace(/\s+/g, "-");
  return s;
};

const slugifyGitLab = (text) => {
  let s = stripDiacritics(text).toLowerCase();
  s = s.replace(/[^a-z0-9\s\-_]/g, "");
  s = s.trim().replace(/\s+/g, "-");
  s = s.replace(/-+/g, "-");
  return s;
};

const slugifyPlain = (text) => {
  let s = stripDiacritics(text).toLowerCase();
  s = s.replace(/[^a-z0-9\s\-_]/g, "");
  s = s.trim().replace(/\s+/g, "-");
  s = s.replace(/-+/g, "-");
  return s.replace(/^-|-$/g, "");
};

const SLUG_FNS = {
  github: slugifyGitHub,
  gitlab: slugifyGitLab,
  plain: slugifyPlain,
};

const parseHeadings = (input) => {
  if (!input) return [];
  const lines = input.split(/\r?\n/);
  const out = [];
  let inFence = false;
  let fenceMarker = "";
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const fenceMatch = line.match(/^(\s{0,3})(```+|~~~+)/);
    if (fenceMatch) {
      const marker = fenceMatch[2][0];
      if (!inFence) {
        inFence = true;
        fenceMarker = marker;
      } else if (marker === fenceMarker) {
        inFence = false;
        fenceMarker = "";
      }
      continue;
    }
    if (inFence) continue;

    const atx = line.match(/^(#{1,6})\s+(.+?)\s*#*\s*$/);
    if (atx) {
      const level = atx[1].length;
      const raw = atx[2].trim();
      if (raw.length === 0) continue;
      out.push({ level, raw, text: stripInlineMarkdown(raw) });
      continue;
    }

    const next = lines[i + 1];
    if (next && line.trim().length > 0) {
      const setextEq = next.match(/^\s{0,3}=+\s*$/);
      const setextDash = next.match(/^\s{0,3}-+\s*$/);
      if (setextEq) {
        out.push({ level: 1, raw: line.trim(), text: stripInlineMarkdown(line.trim()) });
        i += 1;
        continue;
      }
      if (setextDash) {
        out.push({ level: 2, raw: line.trim(), text: stripInlineMarkdown(line.trim()) });
        i += 1;
        continue;
      }
    }
  }
  return out;
};

const buildToc = (headings, opts) => {
  const {
    minLevel,
    maxLevel,
    style,
    slugStyle,
    numbered,
    indentSize,
    skipFirstH1,
  } = opts;

  const slugFn = SLUG_FNS[slugStyle] ?? slugifyGitHub;

  let filtered = headings.filter(
    (h) => h.level >= minLevel && h.level <= maxLevel,
  );

  if (skipFirstH1) {
    const idx = filtered.findIndex((h) => h.level === 1);
    if (idx !== -1) filtered = filtered.filter((_, i) => i !== idx);
  }

  if (filtered.length === 0) return "";

  const seen = new Map();
  const baseLevel = Math.min(...filtered.map((h) => h.level));
  const counters = [];

  const lines = filtered.map((h) => {
    const depth = h.level - baseLevel;
    const indent = " ".repeat(Math.max(0, depth) * indentSize);

    let slug = slugFn(h.text);
    if (!slug) slug = "section";
    const count = seen.get(slug) ?? 0;
    seen.set(slug, count + 1);
    const finalSlug = count === 0 ? slug : `${slug}-${count}`;

    let prefix = "";
    if (numbered) {
      counters.length = depth + 1;
      for (let i = 0; i < counters.length; i += 1) {
        if (counters[i] === undefined) counters[i] = 0;
      }
      counters[depth] = (counters[depth] ?? 0) + 1;
      prefix = `${counters.slice(0, depth + 1).join(".")}. `;
    }

    const label = h.text || h.raw;
    if (style === "links") {
      return `${indent}- ${prefix}[${label}](#${finalSlug})`;
    }
    if (style === "plain") {
      return `${indent}- ${prefix}${label}`;
    }
    return `${indent}- ${prefix}[${label}](#${finalSlug})`;
  });

  return lines.join("\n");
};

const MarkdownTocGenerator = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(SAMPLE_INPUT);
  const [minLevel, setMinLevel] = useState(1);
  const [maxLevel, setMaxLevel] = useState(6);
  const [style, setStyle] = useState("links");
  const [slugStyle, setSlugStyle] = useState("github");
  const [numbered, setNumbered] = useState(false);
  const [indentSize, setIndentSize] = useState(2);
  const [skipFirstH1, setSkipFirstH1] = useState(true);

  const headings = useMemo(() => parseHeadings(input), [input]);

  const safeMin = Math.min(minLevel, maxLevel);
  const safeMax = Math.max(minLevel, maxLevel);

  const output = useMemo(
    () =>
      buildToc(headings, {
        minLevel: safeMin,
        maxLevel: safeMax,
        style,
        slugStyle,
        numbered,
        indentSize,
        skipFirstH1,
      }),
    [headings, safeMin, safeMax, style, slugStyle, numbered, indentSize, skipFirstH1],
  );

  const includedCount = useMemo(
    () =>
      headings.filter((h) => h.level >= safeMin && h.level <= safeMax).length -
      (skipFirstH1 && headings.some((h) => h.level === 1) ? 1 : 0),
    [headings, safeMin, safeMax, skipFirstH1],
  );

  const copy = async () => {
    if (!output) {
      toast.error(t("md_toc_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadSample = () => setInput(SAMPLE_INPUT);
  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("md_toc_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("md_toc_intro")}</p>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label
                  className="text-sm font-medium text-gray-700"
                  htmlFor="md-toc-input"
                >
                  {t("md_toc_input_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={loadSample}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("md_toc_load_sample")}
                  </StandardButton>
                  <StandardButton
                    onClick={clear}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("md_toc_clear")}
                  </StandardButton>
                </div>
              </div>
              <textarea
                id="md-toc-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("md_toc_input_placeholder")}
                rows={12}
                spellCheck={false}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("md_toc_level_range_label")}
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="text-sm text-gray-700 flex items-center gap-2">
                    <span>{t("md_toc_min_level")}</span>
                    <select
                      value={minLevel}
                      onChange={(e) => setMinLevel(Number(e.target.value))}
                      className="p-1 border border-gray-300 rounded-md bg-white"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          H{n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-sm text-gray-700 flex items-center gap-2">
                    <span>{t("md_toc_max_level")}</span>
                    <select
                      value={maxLevel}
                      onChange={(e) => setMaxLevel(Number(e.target.value))}
                      className="p-1 border border-gray-300 rounded-md bg-white"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          H{n}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <span className="text-xs text-gray-500">
                  {t("md_toc_level_range_help")}
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("md_toc_style_label")}
                </label>
                <Radio.Group
                  value={style}
                  onChange={(e) => setStyle(e.target.value)}
                >
                  <Radio.Button value="links">
                    {t("md_toc_style_links")}
                  </Radio.Button>
                  <Radio.Button value="plain">
                    {t("md_toc_style_plain")}
                  </Radio.Button>
                </Radio.Group>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("md_toc_slug_style_label")}
                </label>
                <Radio.Group
                  value={slugStyle}
                  onChange={(e) => setSlugStyle(e.target.value)}
                  disabled={style === "plain"}
                >
                  <Radio.Button value="github">GitHub</Radio.Button>
                  <Radio.Button value="gitlab">GitLab</Radio.Button>
                  <Radio.Button value="plain">
                    {t("md_toc_slug_style_plain")}
                  </Radio.Button>
                </Radio.Group>
                <span className="text-xs text-gray-500">
                  {t("md_toc_slug_style_help")}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={numbered}
                    onChange={(e) => setNumbered(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("md_toc_numbered")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={skipFirstH1}
                    onChange={(e) => setSkipFirstH1(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("md_toc_skip_first_h1")}
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <span>{t("md_toc_indent_size_label")}</span>
                  <select
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value))}
                    className="p-1 border border-gray-300 rounded-md bg-white"
                  >
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("md_toc_output_label")}
                </label>
                <StandardButton
                  onClick={copy}
                  type="primary"
                  size="small"
                  disabled={!output}
                >
                  {t("copy_button")}
                </StandardButton>
              </div>
              <pre className="w-full min-h-[6rem] p-3 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre-wrap break-words text-gray-800">
                {output || (
                  <em className="text-gray-400 not-italic">
                    {t("md_toc_output_empty")}
                  </em>
                )}
              </pre>
              <div className="text-xs text-gray-500">
                {t("md_toc_stats", {
                  headings: headings.length,
                  included: Math.max(0, includedCount),
                })}
              </div>
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("md_toc_note_fences")}</p>
              <p>{t("md_toc_note_slug")}</p>
              <p>{t("md_toc_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default MarkdownTocGenerator;
